import React, { useState, useMemo } from "react";
import { Implementation, ExecutionResults } from "./types";
import { getFrameworkInfo } from "./detail";
import { ScaleIcon, ClockIcon, ArrowDownUp } from "lucide-react";

interface FrameworkComparisonVizProps {
  implementations: Implementation[];
  executionResults: Record<string, ExecutionResults | null>;
  isLoading: boolean;
}

// Define SortOrder type
type SortOrder = "asc" | "desc";

const FrameworkComparisonViz: React.FC<FrameworkComparisonVizProps> = ({
  implementations,
  executionResults,
  isLoading, // Keep isLoading prop
}) => {
  // State for sorting (Called unconditionally)
  const [locSortOrder, setLocSortOrder] = useState<SortOrder>("asc");
  const [runtimeSortOrder, setRuntimeSortOrder] = useState<SortOrder>("asc");

  // Prepare data with framework info for sorting (Moved before conditional return)
  const dataToSort = useMemo(() => {
    return implementations.map((impl) => {
      const framework = impl.framework;
      const loc = impl.code?.loc ?? 0;
      const runtime =
        executionResults[framework]?.execution?.duration_seconds ?? 0;
      const frameworkInfo = getFrameworkInfo(framework);
      const hasRuntimeData = executionResults[framework] !== null;
      return { framework, loc, runtime, frameworkInfo, hasRuntimeData };
    });
  }, [implementations, executionResults]);

  // Sort LOC data (Moved before conditional return)
  const sortedLocData = useMemo(() => {
    return [...dataToSort].sort((a, b) => {
      return locSortOrder === "desc" ? b.loc - a.loc : a.loc - b.loc;
    });
  }, [dataToSort, locSortOrder]);

  // Sort Runtime data (Moved before conditional return)
  const sortedRuntimeData = useMemo(() => {
    return [...dataToSort].sort((a, b) => {
      return runtimeSortOrder === "desc"
        ? b.runtime - a.runtime
        : a.runtime - b.runtime;
    });
  }, [dataToSort, runtimeSortOrder]);

  // Calculate max values (Moved before conditional return)
  const maxLoc = Math.max(...dataToSort.map((d) => d.loc), 1);
  const maxRuntime = Math.max(...dataToSort.map((d) => d.runtime), 0.1);

  // Use isLoading prop directly passed from parent for the initial loading state
  if (isLoading) {
    return <div className="text-center p-4">Loading comparison data...</div>;
  }

  const renderBarChart = (
    title: string,
    icon: React.ReactNode,
    sortedData: typeof dataToSort,
    valueKey: "loc" | "runtime",
    maxValue: number,
    sortOrder: SortOrder,
    setSortOrder: React.Dispatch<React.SetStateAction<SortOrder>>,
    unit: string = ""
  ) => {
    const allDataMissingOrZero = sortedData.every(
      (d) => d[valueKey] === 0 && (valueKey !== "runtime" || !d.hasRuntimeData)
    );

    return (
      // Use theme-agnostic border colors
      <div className="flex-1 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {icon} {title}
          </h3>
          <button
            onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
            className="text-xs p-1 border rounded hover:bg-secondary"
            title={`Sort ${sortOrder === "desc" ? "Ascending" : "Descending"}`}
          >
            <ArrowDownUp className="w-3 h-3 inline-block mr-1" />
            {sortOrder === "desc" ? "Desc" : "Asc"}
          </button>
        </div>
        <div className="space-y-3">
          {sortedData.map((item) => {
            const value = item[valueKey];
            const percentage =
              maxValue > 0
                ? Math.max(0, Math.min(100, (value / maxValue) * 100))
                : 0;
            // Log the runtime value being used for debugging
            if (valueKey === "runtime") {
              console.log(
                `Framework: ${item.framework}, Runtime Value: ${value}, Percentage: ${percentage}%`
              );
            }
            return (
              <div key={item.framework} className="flex items-center gap-3">
                <span
                  className="w-32 text-sm truncate"
                  title={item.frameworkInfo.name}
                >
                  {item.frameworkInfo.name}
                </span>

                {/* Outer bar container - Use fixed gray, theme-agnostic border */}
                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded h-5 overflow-hidden">
                  {/* Inner bar representing value - Use fixed blue, theme-agnostic border */}
                  <div
                    className="bg-accent h-full transition-all duration-300 ease-out border-r border-accent dark:border-accent" // Adjusted colors and border
                    style={{ width: `${percentage}%` }}
                    title={`${value.toFixed(unit === "s" ? 2 : 0)} ${unit}`}
                  />
                </div>
                <span className="text-xs font-mono w-16 text-right">
                  {value.toFixed(unit === "s" ? 2 : 0)}
                  {unit}
                </span>
              </div>
            );
          })}
          {allDataMissingOrZero && (
            <p className="text-xs text-secondary text-center italic">
              No data available for this metric.
            </p>
          )}
        </div>
      </div>
    );
  };

  // Main return statement - Use theme-agnostic border
  return (
    <div className="  rounded  ">
      <div className="flex flex-col md:flex-row gap-4">
        {renderBarChart(
          "Lines of Code (LOC)",
          <ScaleIcon className="w-5 h-5" />,
          sortedLocData,
          "loc",
          maxLoc,
          locSortOrder,
          setLocSortOrder
        )}
        {renderBarChart(
          "Runtime",
          <ClockIcon className="w-5 h-5" />,
          sortedRuntimeData,
          "runtime",
          maxRuntime,
          runtimeSortOrder,
          setRuntimeSortOrder,
          "s"
        )}
      </div>
    </div>
  );
};

export default FrameworkComparisonViz;
